/**
 * Data layer for `self-signup-form-for-affiliates-brandable`. Thin typed fetch wrappers over /api/v1/self-signup-form-for-affiliates-brandable.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface SelfSignupFormForAffiliatesBrandableRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListSelfSignupFormForAffiliatesBrandableResponse {
	count: number;
	items: SelfSignupFormForAffiliatesBrandableRow[];
}

export interface CreateSelfSignupFormForAffiliatesBrandableBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/self-signup-form-for-affiliates-brandable";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListSelfSignupFormForAffiliatesBrandableResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListSelfSignupFormForAffiliatesBrandableResponse>(res);
}

export async function create(body: CreateSelfSignupFormForAffiliatesBrandableBody): Promise<SelfSignupFormForAffiliatesBrandableRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<SelfSignupFormForAffiliatesBrandableRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const selfSignupFormForAffiliatesBrandableQueryKeys = {
	all: ["self-signup-form-for-affiliates-brandable"] as const,
	list: () => [...selfSignupFormForAffiliatesBrandableQueryKeys.all] as const,
	detail: (id: string) => [...selfSignupFormForAffiliatesBrandableQueryKeys.all, id] as const,
};
